import React, { useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAdminStore } from '../../stores/useAdminStore';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { useUIStore } from '../../stores/useUIStore';
import { getValidMonthsForSeason } from '../../utils/seasonHelpers';
import Button from '../ui/Button/Button';
import { BaseModal } from '../shared/Modal/BaseModal';
import { EventFormFields } from './EventFormFields';
import './EventCreateModal.css';

export const EventCreateModal: React.FC = () => {
  const {
    showEventCreateModal,
    selectedSeasonContext,
    closeEventCreateModal
  } = useAdminStore(
    useShallow(s => ({
      showEventCreateModal: s.showEventCreateModal,
      selectedSeasonContext: s.selectedSeasonContext,
      closeEventCreateModal: s.closeEventCreateModal,
    }))
  );
  const { createEvent, isLoading } = useSeasonsStore();
  const { showToast } = useUIStore();

  const [eventName, setEventName] = useState('');
  const [eventMonth, setEventMonth] = useState('');

  const firstInputRef = useRef<HTMLInputElement>(null);

  // Initialize form when modal opens
  useEffect(() => {
    if (selectedSeasonContext && showEventCreateModal) {
      const validMonths = getValidMonthsForSeason(selectedSeasonContext.seasonName);
      setEventMonth(validMonths[0] || 'January');
      setEventName('');
    }
  }, [selectedSeasonContext, showEventCreateModal]);

  if (!showEventCreateModal || !selectedSeasonContext) {
    return null;
  }

  const handleSubmit = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();

    if (!eventName.trim()) {
      showToast('Event name is required', 'error');
      return;
    }

    try {
      await createEvent(
        eventName.trim(),
        eventMonth,
        selectedSeasonContext.seasonId
      );
      showToast(`Event created for ${selectedSeasonContext.seasonName} ${selectedSeasonContext.year}`, 'success');
      closeEventCreateModal();
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const validMonths = getValidMonthsForSeason(selectedSeasonContext.seasonName);

  return (
    <BaseModal
      isOpen={showEventCreateModal}
      onClose={closeEventCreateModal}
      title="Create New Event"
      className="event-create-modal"
      initialFocusRef={firstInputRef}
      data-testid="event-create-modal"
    >
          <div className="season-context-display">
            <p>
              <strong>Creating event for:</strong> {selectedSeasonContext.seasonName} {selectedSeasonContext.year}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="event-create-form">
            <EventFormFields
              name={eventName}
              month={eventMonth}
              months={validMonths}
              disabled={isLoading}
              inputIdPrefix="new-event"
              onNameChange={setEventName}
              onMonthChange={setEventMonth}
              nameInputRef={firstInputRef}
              nameTestId="event-name-input"
              monthTestId="event-month-select"
              helpText={<>Year is automatically set to {selectedSeasonContext.year}</>}
            />

            <div className="form-actions">
              <Button size="action"
                type="submit"
                disabled={isLoading || !eventName.trim()}
                variant="primary"
                data-testid="create-event-button"
              >
                {isLoading ? 'Creating...' : 'Create Event'}
              </Button>
              <Button size="action"
                type="button"
                onClick={closeEventCreateModal}
                variant="secondary"
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
    </BaseModal>
  );
};
